/**
 * Badges shown on profiles. Stat badges are derived from a player's numbers;
 * event badges are granted when something happens (a podium finish, an upset).
 */

export interface BadgeStats {
  games: number;
  wins: number;
  bestRating: number;
  bestStreak: number;
  gold: number;
  /** Non-starter cosmetics owned. */
  items: number;
}

export interface BadgeDef {
  id: string;
  emoji: string;
  /** Stat badges list a condition; event badges are granted explicitly. */
  earned?: (s: BadgeStats) => boolean;
}

export const BADGES: readonly BadgeDef[] = [
  { id: 'first_game', emoji: '🎯', earned: (s) => s.games >= 1 },
  { id: 'first_win', emoji: '🥇', earned: (s) => s.wins >= 1 },
  { id: 'wins_10', emoji: '🔟', earned: (s) => s.wins >= 10 },
  { id: 'wins_50', emoji: '⭐', earned: (s) => s.wins >= 50 },
  { id: 'wins_100', emoji: '🌟', earned: (s) => s.wins >= 100 },
  { id: 'games_100', emoji: '🎖️', earned: (s) => s.games >= 100 },
  { id: 'streak_3', emoji: '🔥', earned: (s) => s.bestStreak >= 3 },
  { id: 'streak_5', emoji: '⚡', earned: (s) => s.bestStreak >= 5 },
  { id: 'rating_1200', emoji: '📈', earned: (s) => s.bestRating >= 1200 },
  { id: 'rating_1500', emoji: '🚀', earned: (s) => s.bestRating >= 1500 },
  { id: 'rating_1800', emoji: '👑', earned: (s) => s.bestRating >= 1800 },
  { id: 'collector_5', emoji: '🧺', earned: (s) => s.items >= 5 },
  { id: 'rich_1000', emoji: '💰', earned: (s) => s.gold >= 1000 },
  // Event badges.
  { id: 'giant_slayer', emoji: '🗡️' },
  { id: 'daily_podium', emoji: '🎁' },
  { id: 'daily_champion', emoji: '🏅' },
  { id: 'weekly_podium', emoji: '🎀' },
  { id: 'weekly_champion', emoji: '🏆' },
];

export type BadgeId = (typeof BADGES)[number]['id'];

const BY_ID = new Map(BADGES.map((b) => [b.id, b]));

export function badge(id: string): BadgeDef | null {
  return BY_ID.get(id) ?? null;
}

/** Stat badges a player qualifies for. */
export function statBadges(stats: BadgeStats): string[] {
  return BADGES.filter((b) => b.earned?.(stats)).map((b) => b.id);
}

/** Beating someone rated this much higher earns the giant-slayer badge. */
export const GIANT_SLAYER_GAP = 200;
